import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from '@remix-run/react';
import { type Screen } from '~/services/layout/index';
import {
  getGeneralContentHookBlockPath,
  getGeneralContentHookEntry,
  getGeneralContentHookState,
  isGeneralContentHookBlockRedirectExcludedPath,
  rememberGeneralContentHookNavigation,
  resolveGeneralContentHookBlockPath,
  type GeneralContentHookRules,
} from '~/services/hooks/general-content-screen-hook';
import { useStableFetcher } from './use-stable-fetcher';
import { addToHistory } from './use-screens-history';

/**
 * Handles general content screen hooks configured through preload plugins.
 * A screen with block_app_navigation_on_all_screens is that hook on every
 * other route, whether or not the viewer is signed in.
 * Storage-key skips are evaluated client-side; endpoint checks run server-side.
 * When serverHooksPending is true, waits so login/parent-lock/storefront
 * hooks can finish before this hook navigates.
 *
 * @param screen - Current screen from loader data.
 * @param screens - All configured screens from loader data.
 * @param serverHooksPending - Whether server preload hooks are still loading or redirecting.
 * @returns `true` while a hook screen is evaluating or redirecting.
 */
export function useGeneralContentScreenHook(
  screen: Screen,
  screens: Array<{
    id: string;
    rules?: GeneralContentHookRules;
    general?: { pathname?: string; custom_url?: string };
  }>,
  serverHooksPending: boolean = false
): boolean {
  const navigate = useNavigate();
  const location = useLocation();

  const hookState = getGeneralContentHookState(screen, screens);
  const isGlobalBlocker =
    !getGeneralContentHookEntry(screen?.hooks?.preload_plugins)?.screen_id &&
    Boolean(hookState.hookScreenId);
  const isActive = hookState.isActive && !hookState.skipByStorageKey;
  const requestUrl = location.search
    ? `${location.pathname}${location.search}`
    : location.pathname;
  const requestIdentity = `${
    screen?.id ?? ''
  }:${requestUrl}:${isActive}:${serverHooksPending}`;
  const previousRequestIdentityRef = useRef(requestIdentity);
  const requestGenerationRef = useRef(0);

  if (previousRequestIdentityRef.current !== requestIdentity) {
    previousRequestIdentityRef.current = requestIdentity;
    requestGenerationRef.current += 1;
  }

  const loadKey = `${requestIdentity}:${requestGenerationRef.current}`;
  const fetcher = useStableFetcher({
    key:
      isActive && screen?.id
        ? `general-content-hook:${screen.id}:${requestUrl}`
        : undefined,
  });
  const [acceptedLoadKey, setAcceptedLoadKey] = useState<string | null>(null);
  const inFlightLoadKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isActive || serverHooksPending) return;

    if (!screen?.id) {
      return;
    }

    if (hookState.requiresServerCheck) {
      inFlightLoadKeyRef.current = null;
      const hookScreenQuery =
        isGlobalBlocker && hookState.hookScreenId
          ? `&hookScreenId=${encodeURIComponent(hookState.hookScreenId)}`
          : '';
      fetcher.load(
        `/api/general-content-hook?screenId=${encodeURIComponent(
          screen.id
        )}${hookScreenQuery}`
      );
      return;
    }

    if (!hookState.redirectPath) return;

    rememberGeneralContentHookNavigation({
      returnPath: location.pathname + location.search,
      redirectPath: hookState.redirectPath,
      blockAppNavigation: Boolean(hookState.blockAppNavigation),
    });

    navigate(hookState.redirectPath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    location.search,
    serverHooksPending,
    screen?.id,
    isActive,
  ]);

  useEffect(() => {
    if (fetcher.state !== 'idle') {
      inFlightLoadKeyRef.current = loadKey;
      return;
    }

    if (inFlightLoadKeyRef.current !== loadKey) return;
    if (!isActive || serverHooksPending) return;

    setAcceptedLoadKey(loadKey);
  }, [fetcher.state, fetcher.data, loadKey, isActive, serverHooksPending]);

  const data =
    fetcher.state === 'idle' && acceptedLoadKey === loadKey
      ? (fetcher.data as { redirectTo?: string; ok?: boolean } | undefined)
      : undefined;

  useEffect(() => {
    if (!isActive || serverHooksPending) return;

    if (data?.redirectTo) {
      rememberGeneralContentHookNavigation({
        returnPath: location.pathname + location.search,
        redirectPath: data.redirectTo,
        blockAppNavigation: Boolean(hookState.blockAppNavigation),
      });

      navigate(data.redirectTo, { replace: true });
      return;
    }

    if (data?.ok) {
      const currentPath = location.pathname + location.search;
      addToHistory(currentPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const isLoading =
    isActive && hookState.requiresServerCheck && acceptedLoadKey !== loadKey;
  const hasRedirect = isActive && Boolean(data?.redirectTo);
  const isRedirectingWithoutServerCheck =
    isActive &&
    !hookState.requiresServerCheck &&
    Boolean(hookState.redirectPath);

  if (!isActive) return false;
  if (serverHooksPending) return true;

  return isLoading || hasRedirect || isRedirectingWithoutServerCheck;
}

/**
 * Keeps a blocking general-content hook in front of the app.
 * Hides the nav bar and footer on the hook screen and sends every other path back until finishHook.
 * The block path is read during render so the other screen is not returned.
 *
 * A non-default language prefix is the same screen as the stored hook path.
 *
 * @param supportedLanguages - App languages. The first entry is the default.
 * @returns Whether the nav bar and footer should be hidden, and whether another path is being sent back.
 */
export function useGeneralContentHookBlock(supportedLanguages: string[] = []): {
  hideNavigation: boolean;
  redirecting: boolean;
} {
  const location = useLocation();
  const navigate = useNavigate();
  const blockPath = getGeneralContentHookBlockPath();
  const redirectPath =
    blockPath &&
    !isGeneralContentHookBlockRedirectExcludedPath(location.pathname)
      ? resolveGeneralContentHookBlockPath({
          pathname: location.pathname,
          blockPath,
          supportedLanguages,
        })
      : null;

  useEffect(() => {
    if (!redirectPath) return;

    navigate(redirectPath, { replace: true });
  }, [redirectPath, navigate]);

  return {
    hideNavigation: Boolean(blockPath) && !redirectPath,
    redirecting: Boolean(redirectPath),
  };
}
